import { useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import {
    BsMetaUtil,
    MAX_LOANS_PAGE_SIZE,
    StrategyExpandedInfo,
    UserStrategiesFilter,
    UserStrategyGroup,
    useStrategiesGroupedByMint,
    useTokenListContext,
    useUserStrategies,
    useUserStrategiesPaginated
} from "@bridgesplit/abf-react";
import {
    Icon,
    MEDIA,
    MobilePortfolioCards,
    MobilePortfolioPagination,
    MobilePortfolioRowConfig,
    Row,
    TABLE_CTA_COLUMN_KEY,
    TableColumn,
    Table,
    Text,
    createRows,
    useCheckboxTableController,
    TableKey,
    HoverableText,
    Tooltip
} from "@bridgesplit/ui";
import {
    ALL_VALUE,
    AllValueType,
    EMPTY_STRING,
    SortDirection,
    formatPercent,
    formatStrategyAddress,
    isAll
} from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { RewardsStrategyApy } from "app/components/points";
import { RateDisplay } from "app/components/points/app/common";
import { useChunkedPaginationController, useClampPaginationOnCountChange } from "app/api/shared";
import { useLenderStrategiesPagination } from "app/api/strategy";
import { AppDialog, useAppDialog } from "app/utils";

import { useNavbarHeight } from "../navbar";
import { TableCardWrapper, TokenImage, getStrategyLoansPath } from "../common";
import StrategyActionsButton, {
    BulkActionsButton,
    CELL_WIDTH_DEFAULTS,
    getDefaultPaginationController,
    GROUP_CTA_CELL,
    GROUP_HEADER_CELL_TOOLTIPS,
    GroupNameCell,
    PairTokenValueCell
} from "./common";
import { LenderLoanStats } from "./stats";
import { SectionCollapse, SectionSkeleton } from "./PortfolioSections";
import { ChipStatPair, PortfolioAllChip, PortfolioTokenChip } from "./mobileCards";

const PAGE_SIZE = 5;

const STRATEGIES_ROW_CONFIG: MobilePortfolioRowConfig = {
    headerDataIndex: "name",
    primaryValueDataIndex: "totalSupply",
    statDataIndexes: ["deployed", "dailyEarnings", "apy"]
};

interface MintNativeTotals {
    totalSupply: number;
    deployed: number;
}

const EMPTY_NATIVE_TOTALS: MintNativeTotals = { totalSupply: 0, deployed: 0 };

function calcMintNativeTotals(items: StrategyExpandedInfo[]): MintNativeTotals {
    return items.reduce<MintNativeTotals>(
        (acc, d) => {
            acc.totalSupply += d.totalBalance + d.strategy.currentDeployedAmount;
            acc.deployed += d.strategy.currentDeployedAmount;
            return acc;
        },
        { totalSupply: 0, deployed: 0 }
    );
}

interface StrategiesSectionProps {
    isExpanded: boolean;
    isUsdValueLoading: boolean;
}

export default function StrategiesSection({ isExpanded, isUsdValueLoading }: StrategiesSectionProps) {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const pagination = useChunkedPaginationController<UserStrategyGroup, UserStrategiesFilter>(
        {
            userAddress: undefined,
            principalMints: undefined,
            showArchived: false,
            sortDirection: SortDirection.Desc
        },
        PAGE_SIZE
    );
    const {
        groups,
        totalGroups,
        aggregate: desktopAggregate,
        isLoading
    } = useUserStrategies(pagination.backendPagination, isMobile);
    useClampPaginationOnCountChange(pagination.clampPageToCount, totalGroups);

    const visibleGroups = useMemo(() => pagination.visibleData(groups) ?? [], [groups, pagination]);
    const visibleMints = useMemo(() => visibleGroups.map((g) => g.principalMint), [visibleGroups]);

    const { detailsByMint, isLoading: detailsLoading } = useStrategiesGroupedByMint(visibleMints, { skip: isMobile });

    const mobile = useMobileStrategiesView(isMobile);
    const aggregate = isMobile ? mobile.aggregate : desktopAggregate;

    const { selected, setSelected } = useCheckboxTableController();
    const selectedAddresses = useMemo(
        () => Array.from(selected).filter((k): k is string => typeof k === "string"),
        [selected]
    );

    const { height: navbarHeight } = useNavbarHeight();

    const tableRows = useMemo(
        () => createRows<UserStrategyGroup>(visibleGroups, (g) => g.principalMint),
        [visibleGroups]
    );

    const nativeTotalsByMint = useMemo(() => {
        const map = new Map<string, MintNativeTotals>();
        if (!detailsByMint) return map;
        for (const [mint, items] of detailsByMint) {
            map.set(mint, calcMintNativeTotals(items));
        }
        return map;
    }, [detailsByMint]);

    const groupedColumnDefs = useMemo(() => getGroupColumnDefs(nativeTotalsByMint), [nativeTotalsByMint]);
    const itemColumnDefs = useMemo(() => getStrategyDetailColumnDefs(), []);

    const bulkAction = <BulkStrategyActionsButton selectedAddresses={selectedAddresses} />;

    if (isUsdValueLoading) return <SectionSkeleton />;

    return (
        <SectionCollapse isExpanded={isExpanded}>
            <LenderLoanStats aggregate={aggregate} />
            <TableCardWrapper flushOnMobile>
                {isMobile ? (
                    <MobilePortfolioCards<UserStrategyGroup, StrategyExpandedInfo>
                        filterLabel="Filter by asset"
                        activeFilter={mobile.activeFilter}
                        onFilterChange={mobile.onFilterChange}
                        groups={mobile.chipRows}
                        renderChip={(row) => <StrategyChipContent group={row?.data} />}
                        groupsLoading={mobile.allGroupsLoading}
                        positions={mobile.positionRows}
                        positionColumns={itemColumnDefs}
                        rowConfig={STRATEGIES_ROW_CONFIG}
                        onPositionNavigatePath={(s) => getStrategyLoansPath(s.strategy)}
                        positionsLoading={mobile.positionsLoading}
                        pagination={mobile.pagination}
                        hasHealth={false}
                        hasSelect
                        selection={{
                            selected,
                            setSelected,
                            getKey: (s) => s.strategy.address,
                            bulkActionNode: bulkAction
                        }}
                        stickyTop={navbarHeight}
                        emptyPlaceholder={`No active ${COPY.STRATEGY_TERM_PLURAL.toLowerCase()} positions`}
                        listLabel={mobile.listLabel}
                        allCard={
                            <PortfolioAllChip
                                loading={!aggregate}
                                count={aggregate?.count}
                                valueUsd={aggregate?.totalSupplyUsd}
                                assetCount={mobile.allGroupsTotal}
                                stats={
                                    <ChipStatPair
                                        loading={!aggregate}
                                        left={<PairTokenValueCell usdAmount={aggregate?.principalDeployedUsd} />}
                                        right={<StrategyChipApy apy={aggregate?.wAvgApy} />}
                                    />
                                }
                            />
                        }
                    />
                ) : (
                    <Table<UserStrategyGroup, StrategyExpandedInfo>
                        rows={tableRows}
                        columns={groupedColumnDefs}
                        grouping={{
                            columns: itemColumnDefs,
                            getItems: (groupKey: TableKey) => detailsByMint?.get(groupKey as string),
                            getDetailKey: (s) => s.strategy.address,
                            isDetailsLoading: detailsLoading
                        }}
                        selection={{ selected, setSelected, expandOnGroupSelect: true }}
                        toolbarActions={bulkAction}
                        paginationProps={{
                            type: "api",
                            pageSize: pagination.pageSize,
                            totalDataCount: totalGroups,
                            controller: getDefaultPaginationController(pagination)
                        }}
                        loading={isLoading}
                        emptyPlaceholder={`No active ${COPY.STRATEGY_TERM_PLURAL.toLowerCase()} positions`}
                        showViewToggle
                        viewToggleStorageKey="PORTFOLIO_STRATEGIES_VIEW"
                    />
                )}
            </TableCardWrapper>
        </SectionCollapse>
    );
}

function useMobileStrategiesView(isMobile: boolean) {
    const [activeFilter, setActiveFilter] = useState<AllValueType | TableKey>(ALL_VALUE);
    const { getMetadata } = useTokenListContext();

    const {
        groups: allGroups,
        totalGroups: allGroupsTotal,
        aggregate,
        isLoading: allGroupsLoading
    } = useUserStrategies(
        {
            showArchived: false,
            sortDirection: SortDirection.Desc,
            page: 0,
            pageSize: MAX_LOANS_PAGE_SIZE
        },
        !isMobile
    );

    const positionsPagination = useLenderStrategiesPagination();
    const {
        positions: positionsChunk,
        totalCount: positionsTotal,
        isLoading: positionsLoading
    } = useUserStrategiesPaginated({
        pagination: positionsPagination.backendPagination,
        principalGroupKey: activeFilter,
        skip: !isMobile
    });
    useClampPaginationOnCountChange(positionsPagination.clampPageToCount, positionsTotal);

    const visiblePositions = useMemo(
        () => positionsPagination.visibleData(positionsChunk),
        [positionsChunk, positionsPagination]
    );

    const chipRows = useMemo(() => createRows<UserStrategyGroup>(allGroups ?? [], (g) => g.principalMint), [allGroups]);
    const positionRows = useMemo(
        () =>
            visiblePositions
                ? createRows<StrategyExpandedInfo>(visiblePositions, (s) => s.strategy.address)
                : undefined,
        [visiblePositions]
    );

    const onFilterChange = (next: AllValueType | TableKey) => {
        setActiveFilter(next);
        positionsPagination.resetPagination();
    };

    const activeGroupSymbol = useMemo(() => {
        if (isAll(activeFilter)) return undefined;
        const metadata = getMetadata(String(activeFilter));
        return metadata ? BsMetaUtil.getSymbol(metadata) : undefined;
    }, [activeFilter, getMetadata]);
    const listLabel = `${activeGroupSymbol ?? "All"} ${COPY.STRATEGY_TERM_PLURAL.toLowerCase()}`;

    const pagination: MobilePortfolioPagination = {
        page: positionsPagination.page,
        setPage: positionsPagination.setPage,
        pageSize: positionsPagination.pageSize,
        totalCount: positionsTotal
    };

    return {
        activeFilter,
        onFilterChange,
        chipRows,
        allGroupsLoading,
        allGroupsTotal,
        aggregate,
        positionRows,
        positionsLoading,
        pagination,
        listLabel
    };
}

function StrategyChipApy({ apy }: { apy: number | undefined }) {
    return <RateDisplay apy={apy} variant="body1" color="body" isFeatured underline={false} />;
}

function StrategyChipContent({ group }: { group: UserStrategyGroup | undefined }) {
    return (
        <PortfolioTokenChip
            metadata={group?.principalMetadata}
            count={group?.aggregate.count}
            valueUsd={group?.aggregate.totalSupplyUsd}
            tokenAmount={group?.totalSupplyNative}
            stats={
                <ChipStatPair
                    loading={!group}
                    left={<PairTokenValueCell usdAmount={group?.aggregate.principalDeployedUsd} />}
                    right={<StrategyChipApy apy={group?.aggregate.wAvgApy} />}
                />
            }
        />
    );
}

function getGroupColumnDefs(nativeTotalsByMint: Map<string, MintNativeTotals>): TableColumn<UserStrategyGroup>[] {
    return [
        {
            dataIndex: "token",
            title: "Asset",
            render: (row) => <GroupNameCell metadata={row.data.principalMetadata} count={row.data.aggregate.count} />,
            sortBy: (row) => BsMetaUtil.getSymbol(row.data.principalMetadata)
        },
        {
            dataIndex: "totalSupply",
            title: "Supplied",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => {
                const { totalSupply } = nativeTotalsByMint.get(row.data.principalMint) ?? EMPTY_NATIVE_TOTALS;
                return (
                    <PairTokenValueCell
                        usdAmount={row.data.aggregate.totalSupplyUsd}
                        tokenAmount={totalSupply}
                        metadata={row.data.principalMetadata}
                    />
                );
            },
            sortBy: (row) => row.data.aggregate.totalSupplyUsd
        },
        {
            dataIndex: "deployed",
            title: "Deployed",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => {
                const { deployed } = nativeTotalsByMint.get(row.data.principalMint) ?? EMPTY_NATIVE_TOTALS;
                return (
                    <PairTokenValueCell
                        usdAmount={row.data.aggregate.principalDeployedUsd}
                        tokenAmount={deployed}
                        metadata={row.data.principalMetadata}
                    />
                );
            },
            sortBy: (row) => row.data.aggregate.principalDeployedUsd
        },
        {
            dataIndex: "dailyEarnings",
            title: "Daily earnings",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) => <PairTokenValueCell usdAmount={row.data.aggregate.expected24hInterestUsd} />,
            sortBy: (row) => row.data.aggregate.expected24hInterestUsd
        },
        {
            dataIndex: "apy",
            title: COPY.APY_TERM,
            tooltip: GROUP_HEADER_CELL_TOOLTIPS.strategies.apy,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.apy,
            render: (row) => formatPercent(row.data.aggregate.wAvgApy),
            sortBy: (row) => row.data.aggregate.wAvgApy
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: GROUP_CTA_CELL
        }
    ];
}

export function getStrategyDetailColumnDefs(): TableColumn<StrategyExpandedInfo>[] {
    return [
        {
            dataIndex: "name",
            title: "Asset",
            render: (row) => <NameCell strategy={row.data} />
        },
        {
            dataIndex: "totalSupply",
            title: "Supplied",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => (
                <PairTokenValueCell
                    usdAmount={row.data.totalSupplyUsd}
                    tokenAmount={row.data.totalBalance + row.data.strategy.currentDeployedAmount}
                    metadata={row.data.principalMetadata}
                />
            )
        },
        {
            dataIndex: "deployed",
            title: "Deployed",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => (
                <PairTokenValueCell
                    usdAmount={row.data.principalDeployedUsd}
                    tokenAmount={row.data.strategy.currentDeployedAmount}
                    metadata={row.data.principalMetadata}
                />
            )
        },
        {
            dataIndex: "dailyEarnings",
            title: "Daily earnings",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) => <PairTokenValueCell usdAmount={row.data.expected24hInterestUsd} />
        },
        {
            dataIndex: "apy",
            title: COPY.APY_TERM,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.apy,
            render: (row) => <RewardsStrategyApy strategyExpanded={row.data} />
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: (row) => <StrategyActionsButton strategyExpanded={row.data} />
        }
    ];
}

/* Strategy cells */
function NameCell({ strategy }: { strategy: StrategyExpandedInfo }) {
    return (
        <Row spacing={1} sx={{ alignItems: "center" }}>
            <TokenImage size="md" metadata={strategy.principalMetadata} />
            <Text variant="body1">{BsMetaUtil.getSymbol(strategy.principalMetadata)}</Text>
            <Tooltip title="Position identifier">
                <HoverableText variant="body2" color="caption">
                    {formatStrategyAddress(strategy.strategy.address)}
                </HoverableText>
            </Tooltip>
        </Row>
    );
}

function BulkStrategyActionsButton({
    selectedAddresses,
    onActionPicked
}: {
    selectedAddresses: string[];
    onActionPicked?: () => void;
}) {
    const { open } = useAppDialog();

    return (
        <BulkActionsButton
            count={selectedAddresses.length}
            actions={[
                {
                    label: "Deposit",
                    icon: <Icon type="arrow-down" />,
                    onClick: () => {
                        open(AppDialog.BulkStrategyDeposit, { strategyAddresses: selectedAddresses });
                        if (onActionPicked) {
                            onActionPicked();
                        }
                    }
                },
                {
                    label: "Withdraw",
                    icon: <Icon type="arrow-diagonal" />,
                    onClick: () => {
                        open(AppDialog.BulkStrategyWithdraw, { strategyAddresses: selectedAddresses });
                        if (onActionPicked) {
                            onActionPicked();
                        }
                    }
                }
            ]}
        />
    );
}
